import React, { useState, useEffect } from 'react';
import { Spinner, Badge, Form } from 'react-bootstrap';
import { FaPlus, FaTimes } from 'react-icons/fa';
import "../../Styles/yellocalfcss/SpecialFees.css";
import ApiConfig from '../../api/ApiConfig';
import { simplePostCall } from '../../api/ApiServices';
import { toast, ToastContainer } from 'react-toastify';

const GenerateSpecialInvoice = () => {
  // --- States ---
  const [studentList, setStudentList] = useState([]); 
  const [categories, setCategories] = useState([]); 
  const [terms, setTerms] = useState([]); 
  
  const [selectedStudentId, setSelectedStudentId] = useState(''); 
  const [selectedStudents, setSelectedStudents] = useState([]); 
  
  const [formData, setFormData] = useState({
    feeCategory: '',
    term: '',
    amount: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  // --- 1. FETCH DATA ON LOAD ---
  useEffect(() => {
    const loadInitialData = async () => {
      setIsFetching(true);
      await Promise.all([
        fetchStudents(),
        fetchCategories(),
        fetchTerms()
      ]);
      setIsFetching(false);
    };
    loadInitialData();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await simplePostCall(ApiConfig.studentlist, JSON.stringify({
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
        user_id: Number(localStorage.getItem("user_id")),
        language: "en"
      }));
      if (response?.studentList) setStudentList(response.studentList);
    } catch (error) { console.error("Students fetch failed", error); }
  };

  const fetchCategories = async () => {
    try {
      const response = await simplePostCall(ApiConfig.categorytype, JSON.stringify({
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
      }));
      if (response?.fee_category) setCategories(response.fee_category);
    } catch (error) { console.error("Categories fetch failed", error); }
  };

  const fetchTerms = async () => {
    try {
      const response = await simplePostCall(ApiConfig.termstype, JSON.stringify({
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
      }));
      if (response?.master_academic) setTerms(response.master_academic);
    } catch (error) { console.error("Terms fetch failed", error); }
  };

  // --- 2. SELECTION HELPERS ---
  const handleAddStudent = () => {
    if (!selectedStudentId) return;
    const student = studentList.find(s => s.student_id.toString() === selectedStudentId);
    if (student && !selectedStudents.find(s => s.id === student.student_id)) {
      setSelectedStudents([...selectedStudents, { id: student.student_id, name: student.student_name }]);
      setSelectedStudentId(''); // Reset dropdown
    }
  };

  const removeStudent = (id) => {
    setSelectedStudents(selectedStudents.filter(s => s.id !== id));
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  // --- 3. SUBMISSION ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (selectedStudents.length === 0) newErrors.student = 'Select at least one student.';
    if (!formData.feeCategory) newErrors.feeCategory = 'Category is required.';
    if (!formData.term) newErrors.term = 'Term is required.';
    if (!formData.amount) newErrors.amount = 'Amount is required.';

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      student_id: selectedStudents.map(s => s.id), 
      fee_invoice_amount: Number(formData.amount),
      fee_category: formData.feeCategory,
      term: formData.term,
    };

    try {
      const response = await simplePostCall(ApiConfig.CreateFeeInvoicespecialfee, JSON.stringify(requestBody));
      if (response?.result) {
        toast.success('Special Invoice created successfully!');
        setFormData({ feeCategory: '', term: '', amount: '' });
        setSelectedStudents([]);
      } else {
        toast.error(response?.message || 'Error creating invoice');
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-fluid SpecialFees-invoice-container my-3">
    <ToastContainer />
      <div className="SelectStudent-invoice-header">
        <h4>Generate Special Invoice</h4>
      </div>

      <form onSubmit={handleSubmit} className="SpecialFees-invoice-form">
        <div className="row mb-3">
          <div className="col-md-6 col-12">
            <label className="form-label">Select Students <span className="text-danger">*</span></label>
            <div className="d-flex gap-2">
              <Form.Select
                className={errors.student ? 'is-invalid' : ''}
                value={selectedStudentId}
                onChange={(e) => setSelectedStudentId(e.target.value)}
              >
                <option value="">Choose Student...</option>
                {studentList.map((s) => (
                  <option key={s.student_id} value={s.student_id}>{s.student_name}</option>
                ))}
              </Form.Select>
              <button type="button" className="btn btn-primary" onClick={handleAddStudent}>
                <FaPlus color="white" />
              </button>
            </div>
            
            {/* Display Selected Students as Chips */}
            <div className="mt-2 d-flex flex-wrap gap-2">
              {selectedStudents.map(student => (
                <Badge key={student.id} bg="light" text="dark" className="border d-flex align-items-center gap-2 p-2">
                  {student.name}
                  <FaTimes className="text-danger cursor-pointer" onClick={() => removeStudent(student.id)} style={{cursor: 'pointer'}} />
                </Badge>
              ))}
            </div>
            {errors.student && <div className="text-danger small mt-1">{errors.student}</div>}
          </div>

          <div className="col-md-6 col-12">
            <label className="form-label">Fee Category <span className="text-danger">*</span></label>
            <select 
              id="feeCategory" 
              className={`form-select ${errors.feeCategory ? 'is-invalid' : ''}`} 
              value={formData.feeCategory} 
              onChange={handleChange}
            >
              <option value="">Select Category</option>
              {categories.map(cat => (
                <option key={cat.fee_category_id} value={cat.fee_category_id}>
                  {cat.fee_category_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6 col-12">
            <label className="form-label">Terms/Semester <span className="text-danger">*</span></label>
            <select 
              id="term" 
              className={`form-select ${errors.term ? 'is-invalid' : ''}`} 
              value={formData.term} 
              onChange={handleChange}
            >
              <option value="">Select Term</option>
              {terms.filter(t => t.terms_status === 'active').map(term => (
                <option key={term.terms_id} value={term.terms_id}>
                  {term.terms_name} ({term.terms_academic_year})
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6 col-12">
            <label className="form-label">Amount <span className="text-danger">*</span></label>
            <input 
              type="number" 
              id="amount" 
              className={`form-control ${errors.amount ? 'is-invalid' : ''}`} 
              value={formData.amount} 
              onChange={handleChange} 
            />
          </div>
        </div>

        <div className="d-flex justify-content-end mt-4">
          <button type="submit" className="btn btn-primary text-white" disabled={isLoading || isFetching}>
            {isLoading ? <Spinner size="sm" /> : 'Generate Invoice'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GenerateSpecialInvoice;